import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';

import ContentHeader from 'components/ContentHeader';
import ItineraryModuleCard from 'components/TrainingGrid/ItineraryModuleCard';

import { getExternalAppChapterUrl } from 'helpers/externalAppUrl';
import { useChapters } from 'hooks/useChapters';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

const ItineraryView = ({ match }) => {
  const { id } = match.params;
  const { itineraries } = useChapters();
  const { appId, lang } = useSettings();
  const { user: { email } } = useUserInfo();

  const itineraryCategories = useMemo(
    () => itineraries.filter((category) => category.id === Number(id)),
    [itineraries, id],
  );

  const { modules = [], chapterIDs = [] } = itineraryCategories[0] || {};

  const filteredModules = useMemo(
    () => modules.filter((moduleItem) => chapterIDs.includes(moduleItem.id)),
    [modules, chapterIDs],
  );

  const { langFile } = lang;
  const locale = `${langFile.toLowerCase()}-${langFile
    .replace('pt', 'BR')
    .replace('es', '41')
    .replace('ko', 'kr')
    .toUpperCase()}`;

  let headerTitle = itineraryCategories[0]?.title;
  if (langFile !== 'en' && itineraryCategories[0]?.translations?.length > 0) {
    headerTitle =
      itineraryCategories[0].translations.find((t) => t.locale === locale)?.title || headerTitle;
  }

  const renderedModules = useMemo(() => {
    const classes = clsx(
      'trainingExtraView__chapter',
      'trainingExtraView__chapter--uncompleted',
    );
    return filteredModules
      .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
      .map((moduleItem) => (
        <ItineraryModuleCard
          key={moduleItem.id}
          appId={appId}
          module={moduleItem}
          redirPath={getExternalAppChapterUrl({
            subAppId: (moduleItem.sub_app && moduleItem.sub_app.id) || moduleItem.sessionAppId || '',
            chapterId: moduleItem.session_chapter_id != null ? moduleItem.session_chapter_id : 0,
            totalCount: (moduleItem.count && moduleItem.count.total_count) != null ? moduleItem.count.total_count : 0,
            highestPage: (moduleItem.progress && moduleItem.progress.highest_page) != null ? moduleItem.progress.highest_page : 0,
            email,
            langFile,
            redirUrl: window.location.href,
          })}
          linkPathSegment="itineraries"
          className={classes}
        />
      ));
  }, [appId, email, filteredModules, langFile]);

  if (itineraryCategories.length === 0) {
    return (
      <div className="contentContainer trainingExtraView">
        <ContentHeader
          header={i18n.translate('contentHeaders.itineraries')}
          subtitle={i18n.translate('pageDescriptions.itineraries')}
          line
        />
        <p>No itinerary found.</p>
      </div>
    );
  }

  return (
    <div className="contentContainer trainingExtraView">
      <ContentHeader
        header={headerTitle}
        subtitle={i18n.translate('pageDescriptions.itineraries')}
        line
      />
      <div className="trainingGrid trainingExtraView__grid">{renderedModules}</div>
    </div>
  );
};

ItineraryView.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default ItineraryView;
